import Quill from "quill";
import { findUrls } from "./urls";

/**
 * Quill エディタ側のベタ書き URL 自動リンク化。
 *
 * 編集中にリンクとして見えることを狙った UX 向けの処理で、リンク化の保証は
 * 表示側の renderRichText が担う。ここで取りこぼしても表示は正しくなる。
 */

const Delta = Quill.import("delta");

// カーソル直前を遡って読む最大文字数。1 打鍵ごとに走るため走査量を一定に抑える。
const CURSOR_SCAN_LIMIT = 2048;

// 半角スペース / タブ / 改行に加えて全角スペースも単語の区切りとして扱う。
const WHITESPACE = /[\s　]/;
const WHITESPACE_ONLY = /^[\s　]+$/;

// 変更差分から「何がどこに挿入されたか」を取り出す。カーソル位置ではなく差分を
// 見るため、選択範囲や IME の状態に左右されない。
function insertionFromDelta(delta) {
  const ops = (delta && delta.ops) || [];
  let index = 0;
  for (const op of ops) {
    if (typeof op.retain === "number") {
      index += op.retain;
    } else if (typeof op.insert === "string") {
      return { index, text: op.insert };
    } else {
      return null;
    }
  }
  return null;
}

/**
 * スペースまたは改行が入力された時点で、その直前の単語が URL ならリンクにする。
 *
 * 入力途中（「https://exa」まで打った状態）でリンク化しないのは、カーソルがリンク
 * 書式の内側に入り、以降の入力までリンクに巻き込まれるのを避けるため。区切り文字を
 * 打たずに保存された URL は表示側の renderRichText が拾う。
 *
 * @param {import('quill').default} quill
 * @param {object} delta text-change イベントの変更差分
 * @returns {boolean} リンク化したか
 */
export function linkifyWordBeforeCursor(quill, delta) {
  const insertion = insertionFromDelta(delta);
  if (!insertion || !WHITESPACE_ONLY.test(insertion.text)) return false;

  // 挿入された区切り文字の直前までが、判定対象の単語の終端。
  const wordEnd = insertion.index;
  if (wordEnd <= 0) return false;

  const scanStart = Math.max(0, wordEnd - CURSOR_SCAN_LIMIT);
  const precedingText = quill.getText(scanStart, wordEnd - scanStart);
  const candidate = precedingText.split(WHITESPACE).pop();
  if (!candidate) return false;

  // 単語に含まれる URL をすべて対象にする。URL が単語の先頭から始まるとは限らず、
  // 「詳細はhttps://example.com」のように直前が日本語だったり、カンマ区切りで
  // 2本並んでいたりする。先頭一致だけを見ると、これらを取りこぼす。
  // 末尾の句読点は findUrls が length から除いている。
  const candidateStart = wordEnd - candidate.length;
  let linked = 0;

  // リンク書式の付与は文字数を変えないため、走査中も index はずれない。
  findUrls(candidate).forEach(({ index, length, href }) => {
    const start = candidateStart + index;
    if (quill.getFormat(start, length).link) return;

    // 区切り文字自体は範囲に含めない。含めると以降の入力がリンク書式を引き継ぐ。
    quill.formatText(start, length, "link", href, "api");
    linked++;
  });

  return linked > 0;
}

/**
 * 本文全体を走査して、まだリンクになっていない URL をリンクにする。
 *
 * 入力時のリンク化は区切り文字を起点にするため、URL を打ち切ったまま保存する、
 * URL の直後に日本語を続ける（「https://example.com をご覧ください」の「を」が
 * 全角スペースでない場合）といったケースを取りこぼす。フォーカスアウト時にこれを
 * 走らせることで、編集画面上でもリンクとして見えるようにする。
 *
 * 既にリンク書式が付いている範囲は書き換えない。本文と違う URL を貼ったリンク
 * （表示は A、リンク先は B）を壊さないため。
 *
 * @param {import('quill').default} quill
 * @returns {number} リンク化した件数
 */
export function linkifyDocument(quill) {
  const urls = findUrls(quill.getText());
  let linkified = 0;

  urls.forEach(({ index, length, href }) => {
    if (quill.getFormat(index, length).link) return;
    quill.formatText(index, length, "link", href, "api");
    linkified++;
  });

  return linkified;
}

/**
 * ユーザー操作による変更を受けて、必要な範囲をリンク化する。
 *
 * 1文字ずつの入力と、まとめて挿入された場合とで扱いを分ける。
 *
 * まとめて挿入されるのは貼り付け・IME の確定・ドラッグ&ドロップで、このうち
 * 貼り付けは clipboard matcher だけでは拾いきれない。Quill はクリップボードに
 * text/html が無いと matcher を通さずテキストをそのまま挿入するため（clipboard.js
 * の convert: `if (!html) return new Delta().insert(text)`）、ターミナルやテキスト
 * エディタからのコピーが素通りしてしまう。挿入の形から判断するこの関数なら、
 * クリップボードの形式に依存せず同じ結果になる。
 *
 * @param {import('quill').default} quill
 * @param {object} delta text-change イベントの変更差分
 * @returns {number} リンク化した件数
 */
export function linkifyUserInput(quill, delta) {
  const insertion = insertionFromDelta(delta);
  if (!insertion) return 0;

  // 1文字ずつの入力。区切り文字を打った時点で直前の単語だけを見る。
  if (insertion.text.length === 1) {
    return linkifyWordBeforeCursor(quill, delta) ? 1 : 0;
  }

  // まとめて入った場合は、どこから来たかに関わらず本文全体を走査する。
  // 既にリンクになっている範囲は触らないので、繰り返し呼んでも二重にならない。
  return linkifyDocument(quill);
}

/**
 * 貼り付けたプレーンテキスト中の URL にリンク書式を付ける。
 * 入力時のリンク化はスペース / 改行を起点にするため、貼り付けだけでは発火しない。
 *
 * Quill の clipboard matcher（Node.TEXT_NODE）として使う。
 *
 * @param {Node} node 変換元のテキストノード
 * @param {object} delta テキストノードから変換された Delta
 * @returns {object} リンク書式を付けた Delta（対象が無ければ引数をそのまま返す）
 */
export function autoLinkPastedText(node, delta) {
  const ops = (delta && delta.ops) || [];
  if (ops.length !== 1 || typeof ops[0].insert !== "string") return delta;

  // <a> の中のテキストは元の href を保つ。matcher は要素より先にテキストノードへ
  // 適用されるため、この時点の delta にはまだ link 属性が無く、attributes だけでは
  // 判定できない。ここで弾かないと <a href="B">https://A</a> の href を A で奪う。
  if (node && node.parentElement && node.parentElement.closest("a")) return delta;

  const attributes = ops[0].attributes || {};
  if (attributes.link) return delta;

  const text = ops[0].insert;
  const urls = findUrls(text);
  if (urls.length === 0) return delta;

  const result = new Delta();
  let cursor = 0;
  urls.forEach(({ index, length, url, href }) => {
    if (index < cursor) return;
    if (index > cursor) {
      result.insert(text.slice(cursor, index), attributes);
    }
    result.insert(url, { ...attributes, link: href });
    cursor = index + length;
  });
  if (cursor < text.length) {
    result.insert(text.slice(cursor), attributes);
  }
  return result;
}
